/**
 * Application entry point
 *
 * - Wraps `<App>` in the Redux `<Provider>` so every component can access the store
 * - Mounts the React tree into `#marketview` (defined in `index.html`)
 * - Suppresses benign "ResizeObserver loop limit exceeded" browser errors that
 *   fire when a ResizeObserver callback takes longer than one animation frame;
 *   the observer self-recovers on the next frame so no real error occurs
 */
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";

import App from "./App";
import { store } from "@store/index";
import { isWeb, isMobile } from "@platform";

import "./index.css";

// On web, no title bar needed; toolbar is sufficient
if (isWeb) {
  document.documentElement.style.setProperty("--titlebar-height", "0px");
  document.documentElement.style.setProperty("--total-header-height", "0px");
}
// On mobile, no title bar at all; chart fills full viewport
if (isMobile) {
  document.documentElement.style.setProperty("--titlebar-height", "0px");
  document.documentElement.style.setProperty("--total-header-height", "0px");
}

// Suppress benign "ResizeObserver loop limit exceeded" errors.
// This is a known browser behaviour that fires when a ResizeObserver
// callback takes longer than a single animation frame — the observer
// will catch up on the next frame, so the error is harmless.
const RESIZE_ERROR = /ResizeObserver loop/;
window.addEventListener("error", (e) => {
  if (RESIZE_ERROR.test(e.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});
window.addEventListener("unhandledrejection", (e) => {
  if (RESIZE_ERROR.test(String(e.reason))) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

const root = ReactDOM.createRoot(
  document.getElementById("marketview") as HTMLElement
);
root.render(
  <Provider store={store}>
    <App />
  </Provider>
);

